// Import maintenance types for use in BuildComponentTypeDefinition
import type { ComponentLifetime, MaintenanceTemplate } from '../Pages/Förvaltning/TemplatesEditor/types';

export enum BaseType {
    Ceiling = "ceiling",
    Chimney = "chimney",
    RoofDrainage = "roof_drainage",
    GroundDrainage = "ground_drainage",
    Facade = "facade",
    Floor = "floor",
    Foundation = "foundation",
    GeoTechnics = "geo_technics",
    Outdoor = "outdoor",
    Roof = "roof",
    RoofSafety = "roof_safety",
    Structural = "structural",
    Wall = "wall",
    Utility = "utility",
    Window = "window",
    Door = "door",
    SheetMetal = "sheet_metal",
    Membrane = "membrane",
    LockMechanism = "lock_mechanism",
    Riser = "riser",
    Balcony = "balcony",
    Miscellaneous = "miscellaneous",
    FixedFurnishing = "fixed_furnishing",
}

export enum RoofSafetyStatus {
    APPROVED = "APPROVED",
    NEEDS_INSPECTION = "NEEDS_INSPECTION",
    FAILED = "FAILED",
    UNKNOWN = "UNKNOWN",
}

// Re-export maintenance types from canonical source to avoid duplication
// Note: These are used by BuildComponentTypeDefinition below
export type {
    DirectionalLifetime,
    ComponentLifetime,
    DirectionalInterval,
    MaintenanceTemplate
} from '../Pages/Förvaltning/TemplatesEditor/types';

export interface BuildComponentTypeDefinition {
    baseType: BaseType;
    translations: {
        sv: string;
        [key: string]: string;
    };
    sortOrder?: number;
    category?: string;  // UtilityCategory or OutdoorCategory value for utility/outdoor types
    maintenance?: {
        lifetime?: ComponentLifetime;
        templates?: Record<string, MaintenanceTemplate>;
    };
    // Note: uvValue and modularSize moved to instance level (component.uvValue, component.areaShapes)
    windowProps?: {
        paneCount?: number;
    };
    doorProps?: {
        doorCategory?: string;
    };
    surfaceProps?: {
        interiorType?: string;
    };
    hazardous?: {
        value?: any;
    };
    buildingRisks?: {
        value?: any;
    };
    material?: {
        value?: any;
    };
    color?: {
        value?: any;
    };
    label?: string;
    comment?: {
        value?: string;
    };
    createdBy?: string;  // UID of the user who created a custom type
    createdAt?: string;  // Stockholm-formatted date
}

export type BuildComponentType = string;

// export enum BuildComponentType {
//     FacadeVerticalSiding = "facade_vertical_siding",
//     ...
// }

// export const COMPONENT_TO_BASE_TYPE: Record<BuildComponentType, BaseType> = {
//     ...
// } as const;

export enum MaintenanceIntervalType {
    years = "years",
    months = "months",
    weeks = "weeks",
    days = "days"
}

// Cost unit for maintenance templates and library templates
export type CostUnit = 'per_sqm' | 'per_unit' | 'per_lm';

// type _ValidateMapping = {
//     [K in BuildComponentType]: K extends keyof typeof COMPONENT_TO_BASE_TYPE ? true : never;
// };

// Helper functions
// export function getBaseType(componentType: BuildComponentType): BaseType {
//     return COMPONENT_TO_BASE_TYPE[componentType];
// }

// export function getComponentsByBaseType(baseType: BaseType): BuildComponentType[] {
//     return Object.entries(COMPONENT_TO_BASE_TYPE)
//         .filter(([, base]) => base === baseType)
//         .map(([component]) => component as BuildComponentType);
// }

export enum RiskConstructionType {
    RiskCrawlSpace = "risk_crawl_space",
    RiskSingleStageSealedRender = "risk_single_stage_sealed_render",
    RiskFloatingFloor = "risk_floating_floor",
    RiskUninsulatedSlabOnGround = "risk_uninsulated_slab_on_ground",
    RiskExternallyVentilatedAttic = "risk_externally_ventilated_attic",
    RiskCompactRoofNoAirGap = "risk_compact_roof_no_air_gap",
    RiskInternalDrainage = "risk_internal_drainage",
    RiskLowSlopeRoof = "risk_low_slope_roof",
    RiskBuiltInWaterConnections = "risk_built_in_water_connections",
    RiskExternalWallNoVaporBarrier = "risk_external_wall_no_vapor_barrier",
    RiskUngroundedElectricalSystem = "risk_ungrounded_electrical_system",
    RiskCablesThroughExternalWallRoof = "risk_cables_through_external_wall_roof",
    RiskRaisedFloorOnSlab = "risk_raised_floor_on_slab",
    RiskTilesOnGypsumInWetRoom = "risk_tiles_on_gypsum_in_wet_room"
}

export enum UtilityCategory {
    Ventilation = "ventilation",
    HeatingCooling = "heating_cooling",
    Elevator = "elevator",
    FireSafety = "fire_safety",
    MechanicalMaintenanceRiskConstructions = "mechanical_maintenance_risk_constructions",
    Electrical = "electrical",
    WaterAndSewage = "water_and_sewage",
    DoorAutomation = "door_automation",
    Appliances = "appliances",
    UtilityOther = "utility_other"
}

export enum OutdoorCategory {
    GroundSurface = "ground_surface",
    PlayEquipment = "play_equipment",
    Boundaries = "boundaries",
    Structures = "structures",
    PermanentFurniture = "permanent_furniture",
    OutdoorOther = "outdoor_other"
}

export enum ElevatorType {
    TractionElevator = "traction_elevator",
    Stairlift = "stairlift",
    HydraulicElevator = "hydraulic_elevator"
}

export enum CardinalDirection {
    North = "north",
    NorthEast = "north_east",
    East = "east",
    SouthEast = "south_east",
    South = "south",
    SouthWest = "south_west",
    West = "west",
    NorthWest = "north_west"
}

export enum ColorEnum {
    ColorWhite = "color_white",
    ColorBlack = "color_black",
    ColorLightGrey = "color_lightGrey",
    ColorDarkGrey = "color_darkGrey",
    ColorBeige = "color_beige",
    ColorYellow = "color_yellow",
    ColorLightBlue = "color_lightBlue",
    ColorDarkBlue = "color_darkBlue",
    ColorLightGreen = "color_lightGreen",
    ColorDarkGreen = "color_darkGreen",
    ColorRed = "color_red",
    ColorFaluRed = "color_faluRed",
    ColorBrown = "color_brown",
    ColorTerracotta = "color_terracotta",
    ColorPink = "color_pink",
    ColorPurple = "color_purple",
    ColorOrange = "color_orange",
    ColorTurquoise = "color_turquoise"
}

export enum HazardType {
    HazardAsbestos = "hazard_asbestos",
    HazardLed = "hazard_led",
    HazardPcb = "hazard_pcb",
    HazardArsenic = "hazard_arsenic",
    HazardBlueConcrete = "hazard_blue_concrete",
    HazardRadon = "hazard_radon"
}

export enum RoofShape {
    RoofShapeGable = "roof_shape_gable",
    RoofShapeMansard = "roof_shape_mansard",
    RoofShapeHip = "roof_shape_hip",
    RoofShapeShed = "roof_shape_shed",
    RoofShapeInvertedShed = "roof_shape_inverted_shed",
    RoofShapeCylindrical = "roof_shape_cylindrical",
    RoofShapeLowSlope = "roof_shape_low_slope",
    RoofShapeFlat = "roof_shape_flat"
}

export enum RoomType {
    RoomGeneral = "room_general",
    RoomKitchen = "room_kitchen",
    RoomAssembly = "room_assembly",
    RoomOffice = "room_office",
    RoomBathroom = "room_bathroom",
    RoomStorage = "room_storage",
    RoomUtility = "room_utility",
    RoomEntryway = "room_entryway",
    RoomHallway = "room_hallway",
    RoomChanging = "room_changing",
    RoomWork = "room_work",
    RoomConference = "room_conference",
    RoomLecture = "room_lecture",
    RoomExhibit = "room_exhibit",
    RoomGenericAttic = "room_generic_attic",
    RoomWarmAttic = "room_warm_attic",
    RoomColdAttic = "room_cold_attic",
    RoomCompactRoof = "room_compact_roof",
    RoomLaundry = "room_laundry",
    RoomStairwell = "room_stairwell",
    RoomElevatorShaft = "room_elevator_shaft",

    // Residential/Living Spaces
    RoomBedroom = "room_bedroom",
    RoomLivingRoom = "room_living_room",
    RoomDiningRoom = "room_dining_room",
    RoomServer = "room_server",
    RoomCleaning = "room_cleaning",
    RoomTrash = "room_trash",
    RoomBikeStorage = "room_bike_storage",
    RoomReception = "room_reception",
    RoomCafeteria = "room_cafeteria",
    RoomGym = "room_gym",
    RoomArchive = "room_archive",
    RoomGarage = "room_garage",
    RoomMedical = "room_medical",
    RoomLibrary = "room_library",
    RoomWorkshop = "room_workshop",
    RoomWc = "room_wc",
    RoomShower = "room_shower",

    // Whole-unit rooms (units plan D13): the room *is* the lägenhet or lokal.
    // Created with the unit; a unit can later be split into real rooms.
    RoomApartment = "room_apartment",
    RoomCommercial = "room_commercial",
}

/** Kind of parking area (buildings/{key}/parking/{areaKey}.kind). Shared with iOS/Android. */
export enum ParkingAreaKind {
    Garage = "garage",
    Outdoor = "outdoor",
    Carport = "carport",
}

/**
 * Where a parking area's drawing comes from (…/parking/{areaKey}.blueprintSource).
 * `level` resolves the linked room's level drawing live; `own` (default when
 * absent) uses the area's own `blueprints`/`currentBlueprintKey`. Shared with iOS/Android.
 */
export enum ParkingBlueprintSource {
    Level = "level",
    Own = "own",
}

/** Kind of parking spot (…/spots/{spotKey}.type). Shared with iOS/Android. */
export enum ParkingSpotType {
    Car = "car",
    Motorcycle = "motorcycle",
    Accessible = "accessible",
    Oversized = "oversized",
}

/** Kind of tenant (tenants/{key}.kind). */
export enum TenantKind {
    Person = "person",
    Company = "company",
}

/**
 * Role of an extra person on a lease (leases/{key}/coTenants/{tenantKey}.role).
 * contractHolder = medhyresgäst, on the contract and jointly liable;
 * resident = medboende, lives there but is not on the contract. Shared with iOS.
 */
export enum CoTenantRole {
    ContractHolder = "contractHolder",
    Resident = "resident",
}

/** Enclosure type of a storage unit (buildings/{key}/storageUnits/{unitKey}.type). Shared with iOS. */
export enum StorageUnitType {
    Mesh = "mesh",
    Board = "board",
    Door = "door",
    Room = "room",
}

/** Kind of unit (buildings/{key}/units/{unitKey}.kind): lägenhet or lokal. Shared with iOS/Android (units plan D2). */
export enum UnitKind {
    Apartment = "apartment",
    Commercial = "commercial",
}

/**
 * Tenure of a unit (…/units/{unitKey}.tenure). Decides who is responsible for
 * the interior and whether the unit can be leased: only `hyresratt` units get
 * leases (units plan D3). Shared with iOS/Android.
 */
export enum UnitTenure {
    Bostadsratt = "bostadsratt",
    Hyresratt = "hyresratt",
    Own = "own",
}

/**
 * What a lease is for (leases/{key}.object.type). `apartment` and
 * `commercialUnit` both point at buildings/{b}/units/{key}; leasesApi sets
 * the type from the unit's kind (units plan D7).
 */
export enum LeaseObjectType {
    ParkingSpot = "parkingSpot",
    StorageUnit = "storageUnit",
    Apartment = "apartment",
    CommercialUnit = "commercialUnit",
    Other = "other",
}

export enum HazardousState {
    Suspected = "SUSPECTED",
    Confirmed = "CONFIRMED"
}

export enum WindowPaneType {
    OnePane = "one_pane",
    TwoPane = "two_pane",
    ThreePane = "three_pane",
}

export enum LevelType {
    Basement = "basement",
    GroundFloor = "ground_floor",
    NthFloor = "nth_floor",
    Attic = "attic"
}

export enum HeritageProtectionLevel {
    Byggnadsminne = "byggnadsminne",
    QMarkt = "q_markt",
    KMarkt = "k_markt",
    Riksintresse = "riksintresse",
    KulturhistorisktVardefull = "kulturhistoriskt_vardefull",
    Kulturreservat = "kulturreservat"
}